import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchTeam, fetchServices } from "../../Redux/WebsiteSlice";
import { locationsAPI, getImageUrl } from "../../services/api";
import { buildMapEmbedSrc } from "../../Utils/mapEmbed";
import { useBookingModal } from "../../context/BookingModalContext";
import Reveal from "../Shared/Reveal";
import s1 from "../../assets/img/s1.jpg";
import "./LocationDetail.scss";

const LocationDetail = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const team = useSelector((state) => state.website.team);
  const allServices = useSelector((state) => state.website.services);
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { openModal } = useBookingModal();

  useEffect(() => {
    dispatch(fetchTeam());
    dispatch(fetchServices());
  }, [dispatch]);

  useEffect(() => {
    const fetchLocation = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await locationsAPI.getById(id);
        setLocation(response.data);
      } catch (err) {
        setError(err.response?.status === 404 ? "Location not found" : "Failed to load location");
      } finally {
        setLoading(false);
      }
    };
    fetchLocation();
  }, [id]);

  if (loading) {
    return (
      <div className="location-detail-loading">
        <div className="loading-message">Loading...</div>
      </div>
    );
  }

  if (error || !location) {
    return (
      <div className="location-detail-error">
        <div className="error-message">
          <h2>Location Not Found</h2>
          <p>{error || "The location you are looking for does not exist."}</p>
          <Link to="/contact" className="site__button site__button_style--solid site__button_shape--default">
            <span className="text">Back to Contact</span>
          </Link>
        </div>
      </div>
    );
  }

  const servicesAtLocation = (allServices || []).filter((service) =>
    (service.locations || []).some((loc) => (typeof loc === "object" ? loc._id : loc) === location._id)
  );

  const aboutParagraphs = (location.aboutText || "")
    .split("\n")
    .map((p) => p.trim())
    .filter(Boolean);

  const staffAtLocation = (team || []).filter((member) =>
    (member.locations || []).some((loc) => (typeof loc === "object" ? loc._id : loc) === location._id)
  );

  const hours = (location.openingHours || []).filter((h) => !h.isClosed && h.hours);

  const addressQuery =
    location.address?.fullAddress ||
    [location.address?.street, location.address?.city, location.address?.state, location.address?.postcode]
      .filter(Boolean)
      .join(", ");

  // Admin can paste a custom Google Maps embed URL for finer control; if they
  // haven't, fall back to a plain address-query embed so a map always shows.
  const mapSrc = buildMapEmbedSrc({ mapEmbedUrl: location.mapEmbedUrl, address: addressQuery });

  return (
    <div className="location-detail">
      <div className="location-detail-breadcrumb">
        <div className="location-detail-breadcrumb__inner">
          <Link to="/">Home</Link>
          <span>/</span>
          <Link to="/contact">Locations</Link>
          <span>/</span>
          <span>{location.name}</span>
        </div>
      </div>

      <section className="location-detail-hero">
        <div className="location-detail-hero__inner">
          <Reveal as="div" direction="left" className="location-detail-hero__text">
            <h1>
              Our {location.name}{location.facilityName ? ` is located within ${location.facilityName}` : ""}
            </h1>
            {aboutParagraphs[0] && <p>{aboutParagraphs[0]}</p>}
            <button
              type="button"
              onClick={() => setTimeout(openModal, 0)}
              className="site__button site__button_style--solid site__button_shape--default"
            >
              <span className="text">Make an Appointment</span>
            </button>
          </Reveal>
          <Reveal as="div" direction="right" delay={0.1} className="location-detail-hero__image">
            <img
              src={location.image ? getImageUrl(location.image) : s1}
              alt={location.name}
              onError={(e) => { e.target.src = s1; }}
            />
          </Reveal>
        </div>
      </section>

      <section className="location-detail-body">
        <div className="location-detail-body__inner">
          <Reveal as="div" direction="up" className="location-detail-main">
            {aboutParagraphs.slice(1).map((para, idx) => (
              <p key={idx} className="location-detail-main__para">{para}</p>
            ))}

            {servicesAtLocation.length > 0 && (
              <>
                <p className="location-detail-main__lead">
                  Our team provides the following services at {location.name}:
                </p>
                <ul className="location-detail-services">
                  {servicesAtLocation.map((service) => (
                    <li key={service._id}>
                      <Link to="/services">{service.title}</Link>
                      {service.description && <span> — {service.description}</span>}
                    </li>
                  ))}
                </ul>
              </>
            )}

            {staffAtLocation.length > 0 && (
              <>
                <h2 className="location-detail-main__heading">{location.name} Team</h2>
                <div className="location-detail-staff-grid">
                  {staffAtLocation.map((member) => (
                    <div key={member._id} className="location-detail-staff-card">
                      <div className="location-detail-staff-card__image">
                        {member.image ? (
                          <img
                            src={getImageUrl(member.image)}
                            alt={member.name}
                            onError={(e) => { e.target.style.display = "none"; }}
                          />
                        ) : (
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                            <circle cx="12" cy="7" r="4" />
                          </svg>
                        )}
                      </div>
                      <h4>{member.name}</h4>
                      {member.role && <p>{member.role}</p>}
                      <button
                        type="button"
                        onClick={() => setTimeout(openModal, 0)}
                        className="location-detail-staff-card__btn"
                      >
                        Meet &amp; Book
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </Reveal>

          <Reveal as="aside" direction="right" delay={0.1} className="location-detail-sidebar">
            <h3>Contact {location.name}</h3>
            {(location.address?.fullAddress || location.address?.street) && (
              <div className="location-detail-sidebar__item">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                  <circle cx="12" cy="10" r="3"></circle>
                </svg>
                <span>
                  {location.address?.fullAddress ||
                    `${location.address?.street}, ${location.address?.city} ${location.address?.state} ${location.address?.postcode}`}
                </span>
              </div>
            )}
            {location.phone && (
              <div className="location-detail-sidebar__item">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.13 11.9a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.04 1h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.04 8.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 21 16.92z"></path>
                </svg>
                <a href={`tel:${location.phone}`}>{location.phone}</a>
              </div>
            )}
            {location.email && (
              <div className="location-detail-sidebar__item">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
                <a href={`mailto:${location.email}`}>{location.email}</a>
              </div>
            )}
            {hours.length > 0 && (
              <div className="location-detail-sidebar__hours">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
                <div>
                  <strong>Opening Hours</strong>
                  <ul>
                    {hours.map((h, i) => (
                      <li key={i}>
                        <span>{h.day}</span>
                        <span>{h.hours}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
            <button
              type="button"
              onClick={() => setTimeout(openModal, 0)}
              className="site__button site__button_style--solid site__button_shape--default location-detail-sidebar__btn"
            >
              <span className="text">Make an Appointment</span>
            </button>
          </Reveal>
        </div>
      </section>

      {mapSrc && (
        <div className="location-detail-map">
          <iframe
            src={mapSrc}
            title={`${location.name} location`}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      )}

      <section className="location-detail-cta">
        <Reveal as="div" direction="up" className="location-detail-cta__inner">
          <h2>Ready to Book at {location.name}?</h2>
          <p>
            Online booking is the quickest way to secure your preferred time and practitioner. Want to meet your
            practitioner first? Browse our team below.
          </p>
          <div className="location-detail-cta__actions">
            <Link to="/our-team" className="site__button site__button_style--outline site__button_shape--default">
              <span className="text">Meet the Team</span>
            </Link>
            <button
              type="button"
              onClick={() => setTimeout(openModal, 0)}
              className="site__button site__button_style--solid site__button_shape--default"
            >
              <span className="text">Book Online</span>
            </button>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default LocationDetail;
